// Formulaires des paramètres du catalogue (identité, calendrier, numérotation, notifications).
import { CaseACocher, Champ, GrilleChamps } from '@cc/ui';
import { useEffect, useState } from 'react';
import { FormulaireParametre, useParametre } from './FormulaireParametre';
import styles from './administration.module.css';

/** Initialise l'état local une fois la version en vigueur chargée. */
function useValeurInitiale<T>(cle: string, defaut: T): [T, (v: T) => void] {
  const p = useParametre(cle);
  const [valeur, setValeur] = useState<T>(defaut);
  const [initialise, setInitialise] = useState(false);
  useEffect(() => {
    if (p.data && !initialise) {
      setValeur({ ...defaut, ...((p.data.valeur as Partial<T> | null) ?? {}) });
      setInitialise(true);
    }
  }, [p.data, initialise, defaut]);
  return [valeur, setValeur];
}

interface Identite {
  nom_usuel: string;
  adresse?: string;
  telephone?: string;
  courriel?: string;
  site_web?: string;
}
const IDENTITE_DEFAUT: Identite = { nom_usuel: '' };

/** Retire les champs facultatifs vides (le schéma refuse une chaîne vide pour un courriel ou une adresse web). */
function nettoyer<T extends object>(v: T): T {
  return Object.fromEntries(Object.entries(v).filter(([, x]) => x !== '' && x !== undefined)) as T;
}

export function OngletIdentite() {
  const [v, setV] = useValeurInitiale('identite_collectivite', IDENTITE_DEFAUT);
  const maj = (cle: keyof Identite) => (e: { target: { value: string } }) => setV({ ...v, [cle]: e.target.value });
  return (
    <FormulaireParametre cle="identite_collectivite" titre="Identité de la collectivité" valeur={nettoyer(v)}>
      {(_p, erreur) => (
        <GrilleChamps>
          <Champ id="param-identite_collectivite-nom_usuel" libelle="Nom usuel" value={v.nom_usuel} onChange={maj('nom_usuel')} erreur={erreur('nom_usuel')} required />
          <Champ id="param-identite_collectivite-adresse" libelle="Adresse" value={v.adresse ?? ''} onChange={maj('adresse')} erreur={erreur('adresse')} facultatif />
          <Champ id="param-identite_collectivite-telephone" libelle="Téléphone" type="tel" value={v.telephone ?? ''} onChange={maj('telephone')} erreur={erreur('telephone')} facultatif />
          <Champ id="param-identite_collectivite-courriel" libelle="Courriel" type="email" value={v.courriel ?? ''} onChange={maj('courriel')} erreur={erreur('courriel')} facultatif />
          <Champ id="param-identite_collectivite-site_web" libelle="Site web" type="url" value={v.site_web ?? ''} onChange={maj('site_web')} erreur={erreur('site_web')} facultatif aide="Adresse complète, par exemple https://mairie.example" />
        </GrilleChamps>
      )}
    </FormulaireParametre>
  );
}

interface Calendrier {
  jours_ouvres: number[];
  heure_ouverture: string;
  heure_fermeture: string;
}
const CALENDRIER_DEFAUT: Calendrier = { jours_ouvres: [1, 2, 3, 4, 5], heure_ouverture: '07:30', heure_fermeture: '15:30' };
const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export function OngletCalendrier() {
  const [v, setV] = useValeurInitiale('calendrier', CALENDRIER_DEFAUT);
  return (
    <FormulaireParametre cle="calendrier" titre="Calendrier local" valeur={v}>
      {(p, erreur) => (
        <>
          {!p.defini ? <p>Valeurs proposées par défaut, non encore enregistrées.</p> : null}
          <fieldset id="param-calendrier-jours_ouvres" className={styles.matrice}>
            <legend>Jours ouvrés</legend>
            <div className={styles.cases}>
              {JOURS.map((j, i) => (
                <CaseACocher
                  key={j}
                  libelle={j}
                  checked={v.jours_ouvres.includes(i + 1)}
                  onChange={() =>
                    setV({ ...v, jours_ouvres: v.jours_ouvres.includes(i + 1) ? v.jours_ouvres.filter((x) => x !== i + 1) : [...v.jours_ouvres, i + 1].sort() })
                  }
                />
              ))}
            </div>
            {erreur('jours_ouvres') ? <p role="alert">{erreur('jours_ouvres')}</p> : null}
          </fieldset>
          <GrilleChamps>
            <Champ id="param-calendrier-heure_ouverture" libelle="Ouverture des services" type="time" value={v.heure_ouverture} onChange={(e) => setV({ ...v, heure_ouverture: e.target.value })} erreur={erreur('heure_ouverture')} required />
            <Champ id="param-calendrier-heure_fermeture" libelle="Fermeture des services" type="time" value={v.heure_fermeture} onChange={(e) => setV({ ...v, heure_fermeture: e.target.value })} erreur={erreur('heure_fermeture') ?? erreur('')} required />
          </GrilleChamps>
          <p>Les jours fériés nationaux proviennent du référentiel ; les jours fériés propres à la collectivité seront saisissables avec le calendrier des délais (lot L0-C).</p>
        </>
      )}
    </FormulaireParametre>
  );
}

export function OngletNumerotation() {
  const [v, setV] = useValeurInitiale('prefixe_numerotation', { prefixe: '' });
  return (
    <FormulaireParametre cle="prefixe_numerotation" titre="Numérotation" valeur={v}>
      {(p, erreur) => (
        <>
          <Champ
            id="param-prefixe_numerotation-prefixe"
            libelle="Préfixe des numéros"
            aide="Majuscules et chiffres, 2 à 8 caractères. Sans préfixe enregistré, le code court de la collectivité est utilisé."
            value={v.prefixe}
            onChange={(e) => setV({ prefixe: e.target.value.toUpperCase() })}
            erreur={erreur('prefixe')}
            maxLength={8}
            required
          />
          <p>
            Format : <strong className="cc-num">{`${v.prefixe || (p.defini ? '' : 'CODE')}-TYPE-${new Date().getFullYear()}-00001`}</strong> (par exemple CA pour un courrier arrivée, DEM
            pour un dossier).
          </p>
        </>
      )}
    </FormulaireParametre>
  );
}

const CANAUX = [
  { code: 'application', libelle: 'Application' },
  { code: 'portail', libelle: 'Portail citoyen' },
  { code: 'courriel', libelle: 'Courriel' },
  { code: 'sms', libelle: 'SMS' },
  { code: 'pwa', libelle: 'Notification mobile' },
];

export function OngletNotifications() {
  const [v, setV] = useValeurInitiale<{ canaux: string[] }>('notifications_par_defaut', { canaux: ['application'] });
  return (
    <FormulaireParametre cle="notifications_par_defaut" titre="Notifications par défaut" valeur={v}>
      {(_p, erreur) => (
        <fieldset id="param-notifications_par_defaut-canaux" className={styles.matrice}>
          <legend>Canaux proposés par défaut aux agents</legend>
          <div className={styles.cases}>
            {CANAUX.map((c) => (
              <CaseACocher
                key={c.code}
                libelle={c.libelle}
                checked={v.canaux.includes(c.code)}
                onChange={() => setV({ canaux: v.canaux.includes(c.code) ? v.canaux.filter((x) => x !== c.code) : [...v.canaux, c.code] })}
              />
            ))}
          </div>
          {erreur('canaux') ? <p role="alert">{erreur('canaux')}</p> : null}
        </fieldset>
      )}
    </FormulaireParametre>
  );
}
